import clsx from 'clsx'
import { useEffect, useState } from 'react'
import Marquee from 'react-fast-marquee'

import mainStyles from 'assets/css/main.module.scss'
import { ReactComponent as GoldenRatioIcon } from 'assets/icons/golden-ratio.svg'
import { ReactComponent as GrowthIcon } from 'assets/icons/growth.svg'
import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg'
import PlayIcon from 'assets/icons/play.svg'
import NmaFoodmarksImg from 'assets/images/nma-foodmarks.png'
import NmaGlobalFantaImg from 'assets/images/nma-global-fanta.png'
import NmaOasisImg from 'assets/images/nma-oasis.png'
import VideoPlaceholder from 'assets/images/video-placeholder.png'
import { ReactComponent as YellowUnderline } from 'assets/images/yellow-underline.svg'
import BigText from 'components/bigText/BigText'
import BottomArc from 'components/bottomArc/BottomArc'
import Bubble from 'components/common/bubble/Bubble'
import { Button } from 'components/common/button/Button'
import Circle from 'components/common/circle/Circle'
import { Flex } from 'components/common/flex/Flex'
import VideoModal from 'components/common/videoModal/VideoModal'
import ExploreYourResourcesBanner from 'components/exploreYourResourcesBanner/ExploreYourResourcesBanner'
import FrameworkWheel from 'components/interactiveNavigation/FrameworkWheel'
import IntroCard from 'components/introCard/IntroCard'
import NmaCard from 'components/nmaCard/NmaCard'
import NmaModal from 'components/nmaModal/NmaModal'
import PartnersSubhead from 'components/partnersSubhead/PartnersSubhead'
import ProjectsSubhead from 'components/projectsSubhead/ProjectsSubhead'
import { Typography } from 'components/uikit/Typography/Typography'
import { useGetUserData } from 'hooks/getUserData'
import { AnalyticsActionType } from 'types/common/analytics.type'
import { EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './Landing.module.scss'
import { NmaModalKeys, nmaModalsData } from './nmaModalsData'

const Landing = () => {
  const { firstName } = useGetUserData()

  const [play, setPlay] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [modalKey, setModalKey] = useState<NmaModalKeys | undefined>(NmaModalKeys.OASIS)

  const [displayCircle, setDisplayCircle] = useState<boolean>(false)

  const onVideoMouseEnter = () => {
    setDisplayCircle(true)
  }

  const onVideoMouseLeave = () => {
    setDisplayCircle(false)
  }

  const handlePlayIconClick = () => {
    setPlay(true)
    setIsOpen(true)
    trackAnalytics({
      type: AnalyticsActionType.action,
      payload: EVENTS.ACTION.LANDING.VIDEO,
    })
  }

  const [modalOpen, setModalOpen] = useState(false)

  const handleShowModal = (open: boolean, modalKey: NmaModalKeys) => {
    setModalKey(modalKey)
    setModalOpen(open)
  }

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [modalOpen])

  const videoSrc =
    'https://facade-api-tccc-prod-coke.playground.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/8460c248-f4b8-4a73-9072-d200f04c11f7/public-download'

  return (
    <div className={clsx('page-landing', styles.landing)}>
      <div className={clsx(styles.header, 'pb-[240px]')}>
        <div className="px-[5px]">
          <BigText />
        </div>
        <div className={clsx(mainStyles.container, '!-mt-[42px]')}>
          <div className="flex flex-col items-start justify-start">
            <div className="relative">
              <span className={styles.title}>
                The <br /> Experience Loop
              </span>
              <Bubble
                text="Stay in the loop"
                className="absolute top-[40px] right-[34px] lg:!block hidden translate-x-full"
                color="#EBFF00"
                fontClassName="italic font-bold text-[14px]"
              />
            </div>
          </div>
          <div>
            <VideoModal
              show={isOpen}
              setShow={setIsOpen}
              videoSrc={videoSrc}
              setPlayVideo={play => setPlay(play)}
              playVideo={play}
            />
          </div>
          <div className={clsx('grid grid-cols-2 gap-x-[40px] mt-[56px]', styles.landingHero)}>
            <Flex
              className="rounded-[8px] cursor-pointer justify-center min-h-[311px] bg-contain bg-top bg-no-repeat"
              style={{ backgroundImage: `url(${VideoPlaceholder})`, backgroundPosition: 'cover', position: 'relative' }}
              onMouseEnter={onVideoMouseEnter}
              onMouseLeave={onVideoMouseLeave}
            >
              {displayCircle && <Circle text="Watch it." textOnHover="Live it." onClick={handlePlayIconClick} />}
              <img
                src={PlayIcon}
                onClick={handlePlayIconClick}
                className={styles.playIcon}
                alt="video-placeholder"
                height={72}
                width={72}
              />
            </Flex>

            <Flex gap={16} align="start" direction="column">
              <Flex align="center">
                <Flex className={styles.counterWrapper}>
                  {'7149'
                    .padStart(4, '0')
                    .split('')
                    .map((digit, index) => (
                      <div className={styles.digitWrapper} key={index}>
                        <p className={styles.counterText}>{digit}</p>
                      </div>
                    ))}
                </Flex>
                <p className={clsx(styles.counterLabel, 'ps-4')}>Projects</p>
              </Flex>
              <Flex>
                <span className={styles.counterDescription}>
                  Capture lightning in a bottle. Take advantage of our process and guide empowered teams to create
                  global experiences.
                </span>
              </Flex>
            </Flex>
          </div>
        </div>
      </div>
      <div className="w-full h-[300px] bg-transparent relative -mt-[160px]">
        <svg width="100%" height="400" viewBox="0 0 100 100" preserveAspectRatio="none" className="-mt-[240px]">
          <path d="M0,100 Q50,-25 100,100 Z" fill="white" stroke="white" />
        </svg>
        <div className="relative z-99 flex flex-col items-center justify-center -mt-[140px]">
          <p className={clsx(styles.ambition)}>
            Ambition Made <br />
            <span className={clsx(styles.yellowHighlight, 'block mt-2')}>Easy</span>
          </p>
          <p className={styles.ambitionSubtext}>
            Together we'll continue to build the best brands in the world, one <br />
            amazing consumer experience at a time.
          </p>
        </div>
      </div>
      <div className={clsx(mainStyles.container, styles.cardSection)}>
        <IntroCard
          title="Weekly+ Growth"
          description="Let's see double. Our goal is to get twice the number of consumers across our portfolio, every week."
          icon={GrowthIcon}
        />
        <IntroCard
          title="Recruitment Opportunity"
          description="We're going to pull in more drinkers and give them more opportunities to enjoy our beverages. It's a win-win."
          icon={PeopleIcon}
        />
        <IntroCard
          title="Human-Centric"
          description="We're talking to people, not personas. Let’s center their perspective within every experience."
          icon={GoldenRatioIcon}
        />
      </div>
      <div className={styles.marqueeSection}>
        <Marquee autoFill>
          <span className={styles.marqueeText}>Get in, {firstName} • We're making experiences •&nbsp;</span>
        </Marquee>
      </div>
      <div className={clsx(styles.exploreExperienceLoopSection, mainStyles.container)}>
        <div className={clsx(styles.title, 'relative')}>
          Explore the Experience Loop
          <div className="absolute -right-[32px] -bottom-[9px]">
            <YellowUnderline style={{ width: '372px' }} />
          </div>
        </div>
        <div className={styles.NavigationCircle}>
          <FrameworkWheel />
        </div>
      </div>
      <div className={mainStyles.container}>
        <ProjectsSubhead />
      </div>
      <PartnersSubhead />

      <div className={clsx(mainStyles.container, 'w-full')}>
        <Flex direction="column" className={styles.proofSection}>
          <div className={clsx(styles.title, 'relative inline-block')}>
            The Proof Is in the Loop
            <Bubble
              color="#E4FF3C"
              text="Wow wow wow"
              direction="down"
              className="-top-[40px] lg:!-right-[20px] lg:!left-auto left-0 right-auto translate-x-full"
            />
          </div>
          <Typography className={styles.text}>
            Teams all over the globe are using the Experience Loop to turn up the heat on their marketing projects.
          </Typography>
        </Flex>
      </div>

      <div className={clsx(mainStyles.container, styles.NmaCardsSection)}>
        <NmaCard
          title="Foodmarks 2024- CCTM Global Meals"
          ouName="ASP, Europe, GCM, Inswa, JSK, LATAM, North America, Global"
          tags={[
            {
              text: 'Winner',
              textColor: 'linear-gradient(89.86deg, #C9252B 0%, #F79900 50%, #6AC9CE 100%)',
              backgroundColor: 'black',
              isGradientColor: true,
            },
            {
              text: '2024',
              textColor: '#1B0E00',
              backgroundColor: '#ABE6FF',
            },
          ]}
          imgSrc={NmaFoodmarksImg}
          onClick={() => {
            handleShowModal(true, NmaModalKeys.FOODMARKS)
            trackAnalytics({
              type: AnalyticsActionType.action,
              payload: EVENTS.ACTION.LANDING.NMA_1,
            })
          }}
          modalKey={NmaModalKeys.FOODMARKS}
          imgCustomPosition={{ objectPosition: ' 0.2px -1px' }}
        />
        <NmaCard
          title="Oasis: Save the Lunch Break"
          ouName="Europe"
          tags={[
            {
              text: 'Winner',
              textColor: 'linear-gradient(89.86deg, #C9252B 0%, #F79900 50%, #6AC9CE 100%)',
              backgroundColor: 'black',
              isGradientColor: true,
            },
            {
              text: '2024',
              textColor: '#1B0E00',
              backgroundColor: '#ABE6FF',
            },
          ]}
          imgSrc={NmaOasisImg}
          onClick={() => {
            handleShowModal(true, NmaModalKeys.OASIS)
            trackAnalytics({
              type: AnalyticsActionType.action,
              payload: EVENTS.ACTION.LANDING.NMA_2,
            })
          }}
          modalKey={NmaModalKeys.OASIS}
          imgCustomPosition={{ objectPosition: ' 0 -1px' }}
        />
        <NmaCard
          title="GLOBAL FANTA HALLOWEEN X BEETLEJUICE"
          ouName="ASP, Europe, JSK, LATAM, North America, Global"
          tags={[
            {
              text: 'Winner',
              textColor: 'linear-gradient(89.86deg, #C9252B 0%, #F79900 50%, #6AC9CE 100%)',
              backgroundColor: 'black',
              isGradientColor: true,
            },
            {
              text: '2024',
              textColor: '#1B0E00',
              backgroundColor: '#ABE6FF',
            },
          ]}
          imgSrc={NmaGlobalFantaImg}
          onClick={() => {
            handleShowModal(true, NmaModalKeys.GLOBAL_FANTA)
            trackAnalytics({
              type: AnalyticsActionType.action,
              payload: EVENTS.ACTION.LANDING.NMA_3,
            })
          }}
          modalKey={NmaModalKeys.GLOBAL_FANTA}
          imgCustomPosition={{ objectPosition: '0 -1px' }}
        />
      </div>
      <div className={clsx(mainStyles.container)}>
        <div className="mt-[40px] mb-[64px]">
          <Button
            className={styles.exploreMoreCTA}
            variant="secondary"
            text="Explore more"
            border
            onClick={() => {
              window.open(process.env.REACT_APP_NMA_AWARDS_URL || '')
              trackAnalytics({
                type: AnalyticsActionType.action,
                payload: EVENTS.ACTION.LANDING.EXPLORE_MORE,
              })
            }}
          />
        </div>
      </div>
      <NmaModal
        key={modalKey}
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setModalKey(undefined)
        }}
        {...(nmaModalsData.find(m => m.modalKey === modalKey) || nmaModalsData[0])}
      />
      <div className="mb-12">
        <ExploreYourResourcesBanner />
      </div>
      <BottomArc />
    </div>
  )
}

export default Landing
